/**
 * Always-isolated Playwright trace ZIP indexing.
 *
 * The child enumerates the central directory into a compact manifest. The host
 * then rehydrates yauzl Entry instances incrementally, preserving the existing
 * lazy entry-body reads without running the expensive enumeration on the main
 * event loop.
 *
 * @module cpu-offload/trace-index
 */

import { randomUUID } from 'node:crypto';
import { createReadStream } from 'node:fs';
import { rm } from 'node:fs/promises';
import { dirname, join } from 'node:path';
import { performance } from 'node:perf_hooks';
import { createInterface } from 'node:readline';
import yauzl, { type Entry } from 'yauzl';
import { withExpensiveSlot } from '../expensive-gate.js';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import { CpuOffloadPool, type CpuOffloadPoolOptions } from './pool.js';
import type { IndexTraceArchiveResult, SerializedTraceArchiveEntry } from './trace-index-task.js';

export const DEFAULT_TRACE_INDEX_TIMEOUT_MS = 60_000;
export const DEFAULT_TRACE_INDEX_RSS_BYTES = 4 * 1024 * 1024 * 1024;

type TraceIndexIsolationOptions = {
  timeoutMs?: number;
  rssBytes?: number;
  rssCheckIntervalMs?: number;
  workerPath?: string;
  readRssBytes?: CpuOffloadPoolOptions['readRssBytes'];
  logger?: BrowserLogger;
  /** @internal Test-only task override for deterministic timeout fixtures. */
  task?: string;
};

export type IsolatedTraceArchiveIndex = {
  entries: Entry[];
  indexDurationMs: number;
  childDurationMs: number;
};

const removeManifest = async (manifestPath: string, logger: BrowserLogger): Promise<void> => {
  try {
    await rm(manifestPath, { force: true });
  } catch (error) {
    logger.warn('[TraceIndex] Failed to remove entry manifest', {
      manifestPath,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

const rehydrateEntry = (serialized: SerializedTraceArchiveEntry): Entry =>
  Object.assign(new yauzl.Entry(), serialized);

const readManifest = async (manifestPath: string): Promise<Entry[]> => {
  const entries: Entry[] = [];
  const lines = createInterface({
    input: createReadStream(manifestPath),
    crlfDelay: Infinity,
  });

  for await (const line of lines) {
    if (!line) continue;
    entries.push(rehydrateEntry(JSON.parse(line) as SerializedTraceArchiveEntry));
  }
  return entries;
};

/**
 * Index a trace archive in a supervised, one-use Bun child.
 *
 * There is deliberately no inline fallback: a timeout, RSS breach, or worker
 * fault rejects ingestion rather than repeating a known event-loop-blocking
 * operation on the host. The manifest is always removed.
 */
export async function indexTraceArchiveIsolated(
  archivePath: string,
  options: TraceIndexIsolationOptions = {}
): Promise<IsolatedTraceArchiveIndex> {
  const logger = options.logger ?? consoleLogger;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TRACE_INDEX_TIMEOUT_MS;
  const manifestPath = join(dirname(archivePath), `.trace-index-${randomUUID()}.jsonl`);
  const startedAt = performance.now();
  const pool = new CpuOffloadPool({
    size: 1,
    defaultTimeoutMs: timeoutMs,
    rssBytes: options.rssBytes ?? DEFAULT_TRACE_INDEX_RSS_BYTES,
    ...(options.rssCheckIntervalMs ? { rssCheckIntervalMs: options.rssCheckIntervalMs } : {}),
    ...(options.workerPath ? { workerPath: options.workerPath } : {}),
    ...(options.readRssBytes ? { readRssBytes: options.readRssBytes } : {}),
    logger,
  });

  try {
    const result = await withExpensiveSlot(() =>
      pool.run<IndexTraceArchiveResult>(
        options.task ?? 'index-trace-archive',
        { archivePath, manifestPath },
        { timeoutMs }
      )
    );
    const entries = await readManifest(manifestPath);
    if (entries.length !== result.entryCount) {
      throw new Error(
        `Trace index manifest entry count mismatch: expected ${result.entryCount}, read ${entries.length}`
      );
    }
    return {
      entries,
      indexDurationMs: performance.now() - startedAt,
      childDurationMs: result.childDurationMs,
    };
  } finally {
    await pool.shutdown();
    await removeManifest(manifestPath, logger);
  }
}
